import React, { useState, useCallback } from "react";
import { ArrowLeft } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { useGetMarketProductDetailsQuery } from "../../services/market";
import LoadingState from "../../components/states/LoadingState";
import ErrorState from "../../components/states/ErrorState";
import MarketProductGallery from "../../components/MarketLab/MarketProductGallery";
import MarketProductInfo from "../../components/MarketLab/MarketProductInfo";
import MarketProductAnalytics from "../../components/MarketLab/MarketProductAnalytics";

const MarketProductDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchBy, setSearchBy] = useState("");
  const [filters, setFilters] = useState([]);
  const [sortBy, setSortBy] = useState("");

  // Fetch market product details using the real API
  const {
    data: productData,
    error,
    isLoading,
    isFetching,
  } = useGetMarketProductDetailsQuery(
    {
      glid: id,
      pageNumber: currentPage,
      pageSize: pageSize,
      search_query: searchBy,
      filters: filters,
      sort_by: sortBy,
    },
    {
      refetchOnMount: true,
      refetchOnReconnect: true,
    }
  );

  // console.log("productData", productData);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const handleServerSearch = useCallback((searchValue) => {
    setSearchBy(searchValue);
    setCurrentPage(1); // Reset to first page when searching
  }, []);

  const handleServerFilter = useCallback((newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filtering
  }, []);

  const handleServerSort = useCallback(
    (sortingState) => {
      if (sortingState.length === 0) {
        if (sortBy !== "") {
          setSortBy("");
          setCurrentPage(1); // Reset to first page when clearing sort
        }
        return;
      }

      const sortInfo = sortingState[0];

      // Format sort_by parameter based on desc value
      const sortValue = sortInfo.desc ? `-${sortInfo.id}` : sortInfo.id;

      if (sortBy !== sortValue) {
        setSortBy(sortValue);
        setCurrentPage(1); // Reset to first page when sorting changes
      } else {
        console.log("⚠️ SORT VALUE UNCHANGED, NOT SETTING");
      }
    },
    [sortBy]
  );

  if (isLoading) {
    return (
      <div className="w-full">
        <LoadingState />
      </div>
    );
  }

  if (error) {
    console.error("Market product details query error:", error);
    return <ErrorState error={error} />;
  }

  if (!productData || !Array.isArray(productData)) {
    return (
      <div className="w-full">
        <div className="p-4 text-center">
          <h2 className="text-xl font-semibold text-gray-600">
            No Data Available
          </h2>
          <p className="text-gray-500 mt-2">No product details found.</p>
        </div>
      </div>
    );
  }

  // Extract different sections from the raw data array
  const header = productData.find((item) => item.type === "page_header");
  const dataSection = productData.find((item) => item.type === "paginated_dgv");
  console.log("dataSection is here", dataSection);
  const chartData = productData.find((item) => item.type === "chart");
  console.log("chartData is here", chartData);

  if (!header || !dataSection) {
    return (
      <div className="w-full">
        <div className="p-4 text-center">
          <h2 className="text-xl font-semibold text-gray-600">
            Incomplete Data
          </h2>
          <p className="text-gray-500 mt-2">
            Missing required header or data section.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 py-8">
      <button
        onClick={() => navigate("/MarketProduct_All")}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8"
      >
        <ArrowLeft className="h-5 w-5" />
        <span>Back to Market Products</span>
      </button>

      {/* Header Section - Same pattern as ProductsPage */}
      <div
        style={{
          backgroundColor: header.Color,
        }}
        className="p-6 mb-8 rounded-lg"
      >
        <h1 className="text-4xl font-bold text-gray-800 mb-2">
          {header.title}
        </h1>
        <h2 className="text-2xl font-medium text-gray-600 mb-2">
          {header["sub-title"]}
        </h2>
        <p className="text-lg text-gray-600">Product ID: {id}</p>
      </div>

      {/* Analytics Section - Pass dataSection like ProductList does */}
      <MarketProductAnalytics
        allAdsData={dataSection}
        chartData={chartData}
        pagination={{
          currentPage: currentPage,
          pageSize: pageSize,
          totalPages: dataSection?.total_page || 1,
          totalItems: dataSection?.total_items || 0,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
        }}
        isLoading={isFetching}
        onServerSearch={handleServerSearch}
        serverSearchValue={searchBy}
        onServerFilter={handleServerFilter}
        serverFilters={filters}
        onServerSort={handleServerSort}
        serverSortBy={sortBy}
        isPaginated={true}
      />
    </div>
  );
};

export default MarketProductDetailsPage;
